import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsDown, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Button } from '../ui/button';
import { PriorityChip } from './TaskFieldChips';
import { DueBadge } from './DueBadge';
import { cn } from '../../lib/utils';

/**
 * Pick active tasks and park them, from inside the Backlog tab.
 *
 * The per-card demote button already covers "this one, now" — this covers the
 * other motion, the periodic sweep where you sit in the backlog and decide what
 * else does not belong on the board. Doing that from the board means leaving the
 * tab you are reasoning in, and re-finding your place after every click.
 *
 * Multi-select rather than select-and-close: the whole value is committing to a
 * batch in one decision, so the count on the confirm button is the point.
 *
 * Built on cmdk so the list is searchable from the keyboard on a project with
 * two hundred open tasks, where scrolling is not a selection strategy.
 */
export default function DemoteTasksDialog({ open, onOpenChange, tasks = [], onConfirm }) {
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    // Reset on close, not on open: leaving a half-made selection behind means
    // the next sweep starts with someone else's decisions already ticked.
    useEffect(() => {
        if (!open) {
            setSelected(new Set());
            setBusy(false);
        }
    }, [open]);

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Newest first. What you are most likely to park is what you most recently
    // added in a burst of optimism.
    const ordered = useMemo(
        () => [...tasks].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
        [tasks]
    );

    const count = selected.size;

    const confirm = async () => {
        if (!count) return;
        setBusy(true);
        try {
            await onConfirm?.([...selected]);
            onOpenChange(false);
        } finally {
            // Not in an else: if the write failed the dialog stays open with the
            // selection intact, so the retry is one click rather than a re-pick.
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg gap-0 overflow-hidden p-0">
                <DialogHeader className="space-y-1 p-4 pb-3 text-left">
                    <DialogTitle className="text-base">Demote to backlog</DialogTitle>
                    <DialogDescription className="text-xs">
                        Pick what you are not scheduling. It stays on the project, out of every board and count, until you promote it.
                    </DialogDescription>
                </DialogHeader>

                <Command
                    // cmdk filters on the rendered value; feeding it the title
                    // keeps search matching what the row actually says.
                    className="border-t border-white/5 bg-transparent"
                >
                    <CommandInput placeholder="Search tasks…" />
                    <CommandList className="max-h-[min(50vh,320px)]">
                        <CommandEmpty className="py-8 text-center text-xs text-muted-foreground">
                            {tasks.length === 0 ? 'Nothing on the board to demote' : 'No tasks match'}
                        </CommandEmpty>
                        <CommandGroup>
                            {ordered.map((task) => {
                                const on = selected.has(task.id);
                                return (
                                    <CommandItem
                                        key={task.id}
                                        value={`${task.title} ${task.id}`}
                                        onSelect={() => toggle(task.id)}
                                        className="gap-2.5"
                                    >
                                        <span
                                            aria-hidden
                                            className={cn(
                                                'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors',
                                                on
                                                    ? 'border-primary bg-primary text-primary-foreground'
                                                    : 'border-white/20'
                                            )}
                                        >
                                            {on ? <Check className="h-3 w-3" /> : null}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-sm">{task.title}</span>
                                        <PriorityChip value={task.priority || 'medium'} />
                                        <DueBadge due={task.due_date} size="sm" />
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>

                <DialogFooter className="gap-2 border-t border-white/5 p-3 sm:justify-between">
                    <span className="hidden self-center text-[11px] tabular-nums text-muted-foreground sm:inline">
                        {count} selected
                    </span>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                            Cancel
                        </Button>
                        <Button onClick={confirm} disabled={!count || busy}>
                            {busy ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <ChevronsDown className="mr-2 h-4 w-4" />
                            )}
                            Demote {count || ''}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
